import { type Route } from 'vue-router';

import {env} from '@/services/env.service';
import { isBrowser } from '@odin/utils';
import type { Domain } from '@/components/domain-checker/dc.types';
import {UATrackingIDs} from '@/services/const';
import {STEPS} from '@/services/const.enum';
import {store, ensureCartLoaded} from '@/store';
import { router } from '@/router';
import { type AppState } from '@/store/const';
import config from '@/services/config.service';
import type { CartItem } from '@/types/api-types.d';
import {logError} from '@/services/logger.service';
import {ROUTENAMES} from '@/consts/const.enum';
import {useCartItemsStore, mapCategories} from '@/store/cartItemsStore';
import Vue from 'vue';
import {WsAnalyticsDirective} from '@odin/vue2-utils';

const win: any = window;
let dataLayer = win.parent.dataLayer || [];

// facebook pixel script load
if (
  env.MODE === 'production' &&
  isBrowser &&
  config.facebookId &&
  document.cookie.includes('ws_privacy_policy_consent=1')
) {
  /* original script (kept for reference on updates):
    <!-- Facebook Pixel Code -->
    <script>
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '697765060292973');
      fbq('track', 'PageView');
    </script>
    <noscript><img height="1" width="1" style="display:none"
      src="https://www.facebook.com/tr?id=697765060292973&ev=PageView&noscript=1"
    /></noscript>
    <!-- End Facebook Pixel Code -->
  */
  (function(f, b, e, v, n: any, t: any, s: any) {
    if (f.fbq) {return;}
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) {f._fbq = n;}
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window as any,
    document as any,
    'script',
    'https://connect.facebook.net/en_US/fbevents.js',
    undefined,
    undefined,
    undefined
  );
  fbq('init', config.facebookId);
  fbq('track', 'PageView');
}


export function setupAnalytics() {
  setupAnalyticsNew();
  setupAnalyticsLegacy();
}

function setupAnalyticsNew() {
  // @ts-ignore
  window.ws_analytics = window.parent?.ws_analytics || { stored: [] };

  Vue.directive('ws-analytics', WsAnalyticsDirective);
}

declare const fbq: any;

// Extract TLD from domain: 'example.co.uk' -> '.co.uk', 'example.sk' -> '.sk'
function getTld(domain: string): string | undefined {
  const firstDotIndex = domain.indexOf('.');
  return firstDotIndex !== -1 ? domain.slice(firstDotIndex) : undefined;
}

// safely get fbq
function _fbq(...args: any) {
  if (typeof fbq !== 'undefined') {
    fbq.apply(fbq, Array.from(args));
  }
}

let eventDataHash = '';

/**
 * Google Universal Analytics setup
 */
export const universalAnalyticsId = () => {
  return !!config.companyCountry ? UATrackingIDs[config.companyCountry] : false;
};
export function initUniversalAnalytics(trackingID: string | boolean) {
  if (!!trackingID) {
    // because it's vanilla JS, it would cause a bunch of any type related errors/warnings
    /* eslint-disable */
    (function (i, s, o: "script", g, r: "ga", a?: HTMLScriptElement, m?: Element) {
      i.GoogleAnalyticsObject = r;
      (i[r] =
          i[r] ||
          function () {
            (i[r].q = i[r].q || []).push(arguments);
          }
      ),
        (i[r].l = new Date().getTime());
      (a = s.createElement(o)), (m = s.getElementsByTagName(o)[0]);
      a.async = true;
      a.src = g;
      m ? m.parentNode?.insertBefore(a, m) : null;
    })(window, document, "script", "//www.google-analytics.com/analytics.js", "ga");

    ga('create', trackingID, 'auto');
    ga('send', 'pageview');
    /* eslint-enable */
  } else {
    logError(
      'Universal Analytics: Missing UA ID, probably config.companyCountry is undefined',
      trackingID
    );
  }
}

// Safely set ga
declare global {
  interface Window {
    ga:any;
    GoogleAnalyticsObject:any;
  }
}
const ga = (...args: (any)) =>  window.ga(...args);

function prepareDataLayer() {
  if (!win.parent.dataLayer) {
    win.parent.dataLayer = dataLayer;
  } else {
    [].push.apply(win.parent.dataLayer, dataLayer);
    dataLayer = win.parent.dataLayer;
  }
}

function pushToDataLayer(eventData: any) {
  const alwaysPresentData = {
    'user_id': store.getters.userId,
  };
  dataLayer.push({...alwaysPresentData, ...eventData});
}


function getItemsGA() {
  const itemsStore = useCartItemsStore();
  return itemsStore.itemsGA;
}

export function setupAnalyticsLegacy() {

  prepareDataLayer();
  const itemsGA = getItemsGA();

  // ref: https://matteo-gabriele.gitbook.io/vue-gtag/methods/events
  router.afterEach((to: Route, from: Route) => {
    // https://loopiagroup.atlassian.net/browse/DPF-1202
    if (from.name === ROUTENAMES.CART && to.name === ROUTENAMES.ORDER) {
      pushToDataLayer({
        event: 'begin_checkout',
        ecommerce: {
          items: [...itemsGA]
        },
      });
    }

    const eventData = {
      event: 'page_view',
      page_type:
        ({
          [ROUTENAMES.CART]: 'cart',
          [ROUTENAMES.DOMAIN_CHECKER]: 'domaincheck',
        } as any)[to.name || ''] || to.name,
      login_status: !!store.state.cart?.user,
    };

    const newEventDataHash = JSON.stringify(eventData);
    if (newEventDataHash !== eventDataHash) {
      eventDataHash = newEventDataHash;
      pushToDataLayer(eventData);
    }
  });

  store.watch(
    (state: AppState) => {
      return state.userStep;
    },
    (step: STEPS) => {
      const trackingSteps: STEPS[] = [
        STEPS.CART,
        STEPS.BILLING_PROFILE,
        STEPS.RECAPITULATION,
      ];
      const index = trackingSteps.indexOf(step);
      if (index > -1) {
        trackStep(index + 1);
      }
      if (step === STEPS.PAYMENT_METHOD || step === STEPS.RECAPITULATION) {
        trackPageViewFromStep(step);
      }
    },
    { immediate: true }
  );

  router.afterEach(() => {
    _fbq('track', 'PageView');
  });
}

export function trackStep(step: number) {
  // ref: https://matteo-gabriele.gitbook.io/vue-gtag/methods/events
  pushToDataLayer({
    event: 'checkout',
    ecommerce: {
      currencyCode: config.currency,
      checkout: {
        actionField: { step },
        products: store.getters.itemsGaee,
      },
    },
  });
}

export function trackPageViewFromStep(step: STEPS) {
  ensureCartLoaded(() => {
    // ensureCartLoaded to get updated info about user is logged or not
    // https://loopiagroup.atlassian.net/browse/DPF-1202?focusedCommentId=151628
    pushToDataLayer({
      event: 'page_view',
      page_type:
        ({
          [STEPS.PAYMENT_METHOD]: 'payment',
          [STEPS.RECAPITULATION]: 'checkout',
        } as any)[step] || step,
      login_status: !!store.state.cart?.user,
    });
  });
}

export function trackPaymentMethod(paymentOptionGroupID: string) {
  const itemsGA = getItemsGA();

  pushToDataLayer({
    event: 'add_payment_info',
    ecommerce: {
      payment_type: paymentOptionGroupID,
      items: [...itemsGA]
    },
  });
}

export function trackPurchase(checkoutData: any) {
  const getters = store.getters;

  /*
   ref:
   https://git.websupport.sk/devel/web/-/blob/master/protected/modules/cart/views/checkout/_partials/orderTracking.php
  */
  _fbq('track', 'Purchase', {
    content_ids: getters.productIds,
    content_type: 'product',
    value: getters.cartPrice,
    currency: config.currency,
  });

  // Init Universal Analytics
  initUniversalAnalytics(universalAnalyticsId());

  // https://loopiagroup.atlassian.net/browse/DPF-1202
  getters.cartItems.forEach((item: CartItem) => {
    // GTM dataLayer per item purchase
    pushToDataLayer({
      event: 'purchase_item',
      item: item.gaee?.name,
      transaction_id: checkoutData.orderId,
      tax: getters.vatValue(item.price?.vatExcluded || 0),
      currency: config.currency,
      value: item.price?.vatExcluded,
      coupon: item.coupon?.code,
      product_service_name: item.properties?.name || item.properties?.domain,
      product_id: item.gaee?.id,
      customer_type: store.state.cart.user?.customerType,
    });

    // Google UA per item transaction
    ga('ecommerce:addItem', {
      id: checkoutData.orderId,
      name: item.gaee?.name,
      sku: item.gaee?.id,
      category: item.gaee?.categories[0],
      price: item.price?.vatExcluded,
      quantity: item.gaee?.quantity
    });
  }
  );

  const itemsGA = getItemsGA();

  // GA4 format
  pushToDataLayer({
    event: 'purchase',
    ecommerce: {
      currency: config.currency,
      value: getters.cartPrice,
      transaction_id: checkoutData.orderId,
      customer_type: store.state.cart.user?.customerType,
      items: [...itemsGA]
    },
  });

  // Google UA purchase event
  ga('ecommerce:addTransaction', {
    id: checkoutData.orderId,
    revenue: getters.cartPrice,
    tax: getters.vatValue(getters.cartPrice).toFixed(2)
  });

  // Only send Google UA data when in production
  if(env.MODE === 'production' || env.MODE === 'testing') {
    ga('ecommerce:send');
  }
}

export function trackInitiateCheckout() {
  // ref: https://git.websupport.sk/devel/web/-/blob/master/protected/modules/cart/views/checkout/index.php
  _fbq('track', 'InitiateCheckout');
}

export function trackSearch({ query }: { query: string }) {
  /*
   ref:
   https://git.websupport.sk/devel/web/-/blob/master/protected/modules/cart/views/checkout/default/domainSearch.php
  */
  _fbq('track', 'Search', {
    search_string: query,
    content_ids: store.getters.productIds,
    content_type: getTld(query),
  });
}

export async function trackAddToCart(product: any) {
  // https://loopiagroup.atlassian.net/browse/DPF-1202
  const item = Array.isArray(product) ? product[0] : product;
  const categories = mapCategories(item.gaee?.categories);

  if(item.hasOwnProperty('gaee')) {
    pushToDataLayer({
      event: 'add_to_cart',
      ecommerce: {
        items: [{
          item_name: item.gaee?.name ?? item.name,
          item_id: item.gaee?.id ?? item.code,
          price: item.gaee?.price ?? item.price.vatExcluded,
          item_brand: item.gaee?.brand ?? 'Websupport',
          ...categories,
          quantity: item.gaee?.quantity ?? 1,
        }]
      },
    });
  }
  else {
    logError('add_to_cart GA event failed: gaee prop missing', item);
  }
}

export async function trackRemoveFromCart(item: CartItem) {
  const categories = mapCategories(item.gaee?.categories);

  // https://loopiagroup.atlassian.net/browse/DPF-1202
  pushToDataLayer({
    event: 'remove_from_cart',
    ecommerce: {
      items: [{
        item_name: item.gaee.name,
        item_id: item.gaee.id,
        price: item.gaee.price,
        item_brand: item.gaee.brand,
        ...categories,
        quantity: item.gaee.quantity,
      }],
    },
  });
}

export function trackSearchResult({
  query,
  result,
}: {
  query: string;
  result: Domain;
}) {
  // https://loopiagroup.atlassian.net/browse/DPF-1202
  let status: string;
  if (query !== result.name) {
    status = 'no-tld';
  } else if (result.availableForPurchase) {
    status = 'available';
  } else {
    status = 'not_available';
  }

  pushToDataLayer({
    event: 'domain_check',
    currency: config.currency,
    product_service_name: query,
    price: result.price?.actualPrice || 0,
    status,
  });
}

// Commented out for now till we investigate other approaches
// due to session cookies GTM fails during the order for new user
// and never sends data.
//
// export function trackNewProfile(
//   formId: string,
//   emailInputId: string,
//   emails: string[],
//   url: string,
// ) :void {
//   pushToDataLayer({
//     event: 'form_submit',
//     eventModel: {
//       form_id: formId,
//       form_destination: url,
//       email_id: emailInputId,
//       emails: emails,
//     },
//   });
// };
